import React, { useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { useLanguage } from "../contexts/LanguageContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import { useSpamSchutz, Honigtopf } from "../components/SpamSchutz";
import { 
  ArrowRight, 
  ExternalLink, 
  Mail, 
  Star,
  TrendingUp,
  Users,
  Globe,
  Shield,
  CheckCircle2,
  AlertCircle,
  Building2,
  Factory,
  Banknote,
  Car,
  Truck,
  Megaphone,
  BarChart3,
  Server,
  HeadphonesIcon,
  Calculator,
  Database,
  Crown,
  Heart,
  GraduationCap,
  Loader2,
  Brain,
  Zap,
  Lock,
  Mic,
  Network,
  Phone,
  Target,
  FileText
} from "lucide-react";

const seoConfig = {
  title: "Spin-offs | KI-Ventures & Investments | BROVKO",
  description: "BROVKO Spin-offs und KI-Ventures. Innovative KI-Produkte und Technologien aus unserer Forschung und Entwicklung.",
  keywords: "KI Spin-offs, AI Ventures, KI Investments, KI Startups, BROVKO Ventures",
  canonicalUrl: "/spin-offs"
};

const SpinOffs = () => {
  const { t } = useLanguage();

  // Investor form state
  const { honigtopf, spamFelder } = useSpamSchutz();
  const [investorData, setInvestorData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: ''
  });
  const [investorStatus, setInvestorStatus] = useState('idle'); // idle, submitting, success, error
  const [investorErrors, setInvestorErrors] = useState({});

  // Investor form handlers
  const handleInvestorInputChange = (e) => {
    const { name, value } = e.target;
    setInvestorData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (investorErrors[name]) {
      setInvestorErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateInvestorForm = () => {
    const errors = {};
    
    if (!investorData.firstName.trim()) {
      errors.firstName = t('knowledge.download.errors.firstName');
    }
    
    if (!investorData.lastName.trim()) {
      errors.lastName = t('knowledge.download.errors.lastName');
    }
    
    if (!investorData.email.trim()) {
      errors.email = t('knowledge.download.errors.email');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(investorData.email)) {
      errors.email = t('knowledge.download.errors.emailInvalid');
    }
    
    if (!investorData.company.trim()) {
      errors.company = t('knowledge.download.errors.company');
    }
    
    if (!investorData.phone.trim()) {
      errors.phone = t('knowledge.download.errors.phone');
    }
    
    setInvestorErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInvestorSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateInvestorForm()) {
      return;
    }
    
    setInvestorStatus('submitting');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/investor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...(await spamFelder()),
          firstName: investorData.firstName,
          lastName: investorData.lastName,
          email: investorData.email,
          company: investorData.company,
          investmentFocus: "AI Startups", // Default for this form
          investmentStage: "Early Stage"  // Default for this form
        }),
      });

      const result = await response.json();

      if (result.success) {
        setInvestorStatus('success');
        setInvestorData({ firstName: '', lastName: '', email: '', company: '', phone: '' });
        setInvestorErrors({});
        
        // Auto-hide success message after 7 seconds
        setTimeout(() => {
          setInvestorStatus('idle');
        }, 7000);
      } else {
        // Handle specific errors from backend
        if (result.message) {
          // Subscription error handled in UI
          // You could show specific error messages here if needed
        }
        setInvestorStatus('error');
        
        // Auto-hide error after 5 seconds
        setTimeout(() => {
          setInvestorStatus('idle');
        }, 5000);
      }
      
    } catch (error) {
      // Network error handled in UI
      setInvestorStatus('error');
      
      // Auto-hide error after 5 seconds
      setTimeout(() => {
        setInvestorStatus('idle');
      }, 5000);
    }
  };

  return (
    <div className="theme-brand bg-surface text-ink min-h-screen bg-surface-sunken">
      <SEOHead {...seoConfig} />
      <Header />
      
      {/* Full Screen Success Modal for Investor Newsletter */}
      {investorStatus === 'success' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface rounded-2xl p-12 mx-4 max-w-2xl w-full text-center shadow-2xl">
            <CheckCircle2 className="h-24 w-24 text-green-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold text-ink mb-4">
              Inquiry Successfully Sent!
            </h2>
            <p className="text-xl text-ink-muted mb-8 leading-relaxed">
              Thank you for your interest in our spin-offs! We will contact you shortly with detailed information and business opportunities.
            </p>
            <Button 
              onClick={() => setInvestorStatus('idle')}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 text-lg"
            >
              Close
            </Button>
          </div>
        </div>
      )}
      
      {/* Hero Section */}
      <section className="pt-40 pb-20 bg-gradient-to-br from-slate-900 to-blue-900">
        <div className="container mx-auto px-4">
          <div className=" text-white">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              {t('spinoffs.hero.title')}
            </h1>
            <p className="text-xl md:text-2xl text-ink-muted max-w-5xl mx-auto">
              {t('spinoffs.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-20 bg-surface">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch] mb-16">
            <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-ink mb-6">
              {t('spinoffs.intro.title')}
            </h2>
            <p className="text-xl text-ink-muted leading-relaxed">
              {t('spinoffs.intro.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-surface-raised/40">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch] mb-16">
            <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-ink mb-6">
              {t('spinoffs.process.title')}
            </h2>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              
              {/* Step 1 - Idea Incubation */}
              <div className="text-center">
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Brain className="h-8 w-8 text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-blue-500/15 rounded-full flex items-center justify-center">
                    <span className="text-blue-400 font-bold text-sm">1</span>
                  </div>
                </div>
                <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-ink mb-3">
                  {t('spinoffs.process.step1')}
                </h3>
                <p className="text-ink-muted">
                  {t('spinoffs.process.step1.desc')}
                </p>
              </div>

              {/* Step 2 - Product Development */}
              <div className="text-center">
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-brand rounded-full flex items-center justify-center mx-auto mb-4">
                    <Zap className="h-8 w-8 text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-brand/15 rounded-full flex items-center justify-center">
                    <span className="text-brand-400 font-bold text-sm">2</span>
                  </div>
                </div>
                <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-ink mb-3">
                  {t('spinoffs.process.step2')}
                </h3>
                <p className="text-ink-muted">
                  {t('spinoffs.process.step2.desc')}
                </p>
              </div>

              {/* Step 3 - Scaling */}
              <div className="text-center">
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <TrendingUp className="h-8 w-8 text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-purple-500/15 rounded-full flex items-center justify-center">
                    <span className="text-purple-400 font-bold text-sm">3</span>
                  </div>
                </div>
                <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-ink mb-3">
                  {t('spinoffs.process.step3')}
                </h3>
                <p className="text-ink-muted">
                  {t('spinoffs.process.step3.desc')}
                </p>
              </div>

              {/* Step 4 - Market Launch */}
              <div className="text-center">
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Target className="h-8 w-8 text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500/15 rounded-full flex items-center justify-center">
                    <span className="text-green-400 font-bold text-sm">4</span>
                  </div>
                </div>
                <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-ink mb-3">
                  {t('spinoffs.process.step4')}
                </h3>
                <p className="text-ink-muted">
                  {t('spinoffs.process.step4.desc')}
                </p>
              </div>
            </div>

            {/* Process Flow Arrow */}
            <div className="hidden lg:block">
              <div className="flex justify-center items-center mt-8">
                <div className="flex items-center space-x-4">
                  <ArrowRight className="h-6 w-6 text-blue-400" />
                  <ArrowRight className="h-6 w-6 text-brand-400" />
                  <ArrowRight className="h-6 w-6 text-purple-400" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Current Start-Ups Header */}
      <section className="py-20 md:py-28 bg-surface">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-ink mb-6">
              {t('spinoffs.current.title')}
            </h2>
            <p className="text-xl text-ink-muted leading-relaxed">
              {t('spinoffs.current.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* AIWizz Section */}
      <section className="py-20 bg-surface-raised">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              
              {/* Left Column - Content */}
              <div>
                <div className="inline-flex items-center bg-blue-500/15 text-blue-300 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                  <Brain className="h-4 w-4 mr-2" />
                  {t('spinoffs.aiwizz.badge')}
                </div>
                
                <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-ink mb-6">
                  {t('spinoffs.aiwizz.title')}
                </h2>
                
                <p className="text-xl text-ink-muted mb-6">
                  {t('spinoffs.aiwizz.subtitle')}
                </p>
                
                <p className="text-ink-muted mb-8 leading-relaxed">
                  {t('spinoffs.aiwizz.description')}
                </p>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <Shield className="h-6 w-6 text-blue-400 mt-0.5 flex-shrink-0" />
                    <p className="text-ink-muted">{t('spinoffs.aiwizz.feature1')}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Network className="h-6 w-6 text-blue-400 mt-0.5 flex-shrink-0" />
                    <p className="text-ink-muted">{t('spinoffs.aiwizz.feature2')}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Zap className="h-6 w-6 text-blue-400 mt-0.5 flex-shrink-0" />
                    <p className="text-ink-muted">{t('spinoffs.aiwizz.feature3')}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mic className="h-6 w-6 text-blue-400 mt-0.5 flex-shrink-0" />
                    <p className="text-ink-muted">{t('spinoffs.aiwizz.feature4')}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Users className="h-6 w-6 text-blue-400 mt-0.5 flex-shrink-0" />
                    <p className="text-ink-muted">{t('spinoffs.aiwizz.feature5')}</p>
                  </div>
                </div>

                <a
                  href="https://www.aiwizz.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3">
                    {t('spinoffs.aiwizz.cta')}
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </div>

              {/* Right Column - AIWizz Screenshot */}
              <div>
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-2xl transform -rotate-3"></div>
                  <div className="relative bg-surface p-4 rounded-2xl shadow-2xl">
                    <img 
                      src="/assets/images/spinoff-vox2doc.png"
                      alt="AIWizz Platform Screenshot"
                      className="w-full h-auto rounded-xl shadow-lg"
                    />
                  </div>
                  <div className="absolute -bottom-4 -right-4 bg-surface p-3 rounded-xl shadow-lg">
                    <div className="flex items-center gap-2">
                      <Brain className="h-5 w-5 text-blue-400" />
                      <span className="text-sm font-medium text-blue-300">AIWizz Platform</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AICaseHub Section */}
      <section className="py-20 bg-surface">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              
              {/* Left Column - Features Visualization */}
              <div>
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-teal-400 to-green-500 rounded-2xl transform rotate-3"></div>
                  <div className="relative bg-surface p-8 rounded-2xl shadow-2xl">
                    <img 
                      src="/assets/images/spinoff-aiwizz.png"
                      alt="AICaseHub.io Screenshot"
                      className="w-full h-auto rounded-xl shadow-lg"
                    />
                    <div className="mt-4 text-center">
                      <div className="inline-flex items-center bg-gradient-to-r from-brand/15 to-green-500/15 px-4 py-2 rounded-full">
                        <Target className="h-5 w-5 text-brand-400 mr-2" />
                        <span className="text-sm font-medium text-brand-300">300+ Use Cases</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Content */}
              <div>
                <div className="inline-flex items-center bg-brand/15 text-brand-300 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                  <FileText className="h-4 w-4 mr-2" />
                  {t('spinoffs.aicasehub.badge')}
                </div>
                
                <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-ink mb-6">
                  {t('spinoffs.aicasehub.title')}
                </h2>
                
                <p className="text-xl text-ink-muted mb-6">
                  {t('spinoffs.aicasehub.subtitle')}
                </p>
                
                <p className="text-ink-muted mb-8 leading-relaxed">
                  {t('spinoffs.aicasehub.description')}
                </p>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <Globe className="h-6 w-6 text-brand-400 mt-0.5 flex-shrink-0" />
                    <p className="text-ink-muted">{t('spinoffs.aicasehub.feature1')}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <BarChart3 className="h-6 w-6 text-brand-400 mt-0.5 flex-shrink-0" />
                    <p className="text-ink-muted">{t('spinoffs.aicasehub.feature2')}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <FileText className="h-6 w-6 text-brand-400 mt-0.5 flex-shrink-0" />
                    <p className="text-ink-muted">{t('spinoffs.aicasehub.feature3')}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone className="h-6 w-6 text-brand-400 mt-0.5 flex-shrink-0" />
                    <p className="text-ink-muted">{t('spinoffs.aicasehub.feature4')}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Star className="h-6 w-6 text-brand-400 mt-0.5 flex-shrink-0" />
                    <p className="text-ink-muted">{t('spinoffs.aicasehub.feature5')}</p>
                  </div>
                </div>

                <a
                  href="https://aicasehub.io"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="bg-brand hover:bg-brand-600 text-ink-onBrand px-8 py-3">
                    {t('spinoffs.aicasehub.cta')}
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Investor Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-slate-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="max-w-[64ch] mb-12">
              <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-white mb-6">
                {t('spinoffs.investors.title')}
              </h2>
              <p className="text-xl text-ink-muted max-w-[62ch]">
                {t('spinoffs.investors.subtitle')}
              </p>
            </div>

            <Card className="border-0 shadow-2xl bg-surface">
              <CardContent className="p-8">
                {investorStatus === 'error' ? (
                  <div className="text-center py-20 md:py-28">
                    <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
                    <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-ink mb-2">
                      Anfrage fehlgeschlagen
                    </h3>
                    <p className="text-ink-muted text-lg mb-6">
                      Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.
                    </p>
                    <Button 
                      onClick={() => setInvestorStatus('idle')}
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      Erneut versuchen
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleInvestorSubmit} className="space-y-6">
                    <Honigtopf {...honigtopf} />
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="firstName" className="block text-sm font-medium text-ink-muted mb-2">
                          {t('spinoffs.investors.firstName')} *
                        </label>
                        <Input
                          id="firstName"
                          name="firstName"
                          type="text"
                          value={investorData.firstName}
                          onChange={handleInvestorInputChange}
                          className={investorErrors.firstName ? "border-red-400" : ""}
                          required
                        />
                        {investorErrors.firstName && (
                          <p className="text-red-400 text-sm mt-1">{investorErrors.firstName}</p>
                        )}
                      </div>
                      
                      <div>
                        <label htmlFor="lastName" className="block text-sm font-medium text-ink-muted mb-2">
                          {t('spinoffs.investors.lastName')} *
                        </label>
                        <Input
                          id="lastName"
                          name="lastName"
                          type="text"
                          value={investorData.lastName}
                          onChange={handleInvestorInputChange}
                          className={investorErrors.lastName ? "border-red-400" : ""}
                          required
                        />
                        {investorErrors.lastName && (
                          <p className="text-red-400 text-sm mt-1">{investorErrors.lastName}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-ink-muted mb-2">
                        {t('spinoffs.investors.email')} *
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={investorData.email}
                        onChange={handleInvestorInputChange}
                        className={investorErrors.email ? "border-red-400" : ""}
                        required
                      />
                      {investorErrors.email && (
                        <p className="text-red-400 text-sm mt-1">{investorErrors.email}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-ink-muted mb-2">
                        {t('spinoffs.investors.company')} *
                      </label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        value={investorData.company}
                        onChange={handleInvestorInputChange}
                        className={investorErrors.company ? "border-red-400" : ""}
                        required
                      />
                      {investorErrors.company && (
                        <p className="text-red-400 text-sm mt-1">{investorErrors.company}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-ink-muted mb-2">
                        {t('spinoffs.investors.phone')} *
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={investorData.phone}
                        onChange={handleInvestorInputChange}
                        className={investorErrors.phone ? "border-red-400" : ""}
                        required
                      />
                      {investorErrors.phone && (
                        <p className="text-red-400 text-sm mt-1">{investorErrors.phone}</p>
                      )}
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-gradient-to-r from-gray-800 to-slate-700 hover:from-gray-900 hover:to-slate-800 text-white py-3 text-lg"
                      disabled={investorStatus === 'submitting'}
                    >
                      {investorStatus === 'submitting' ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          {t('spinoffs.investors.submitting')}
                        </>
                      ) : (
                        <>
                          <Mail className="mr-2 h-5 w-5" />
                          {t('spinoffs.investors.submit')}
                        </>
                      )}
                    </Button>

                    <p className="text-center text-sm text-ink-faint mt-4">
                      {t('spinoffs.investors.privacy')}
                    </p>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SpinOffs;